import apiClient from "../../../../../components/api/apiClient";
import { apiService } from "../../../../../components/api/apiService";
import {
  NIL_GUID,
  tenantDefaultsService,
  type CopilotKeyDto,
} from "../../../../../components/api/tenantDefaultsService";
import {
  buildIntentDefinitionPayload,
  parseIntentDefinitionRecord,
  parseIntentDefinitionResponse,
  type IntentDefinitionPayload,
  type IntentDefinitionRecord,
  type IntentDefinitionListItem,
  type IntentFormSnapshot,
  type ParsedIntentLoadResult,
  validateIntentForm,
} from "./intentDefinitionPayload";

const KNOWLEDGE_BASES_PATH = "/api/FeatureHelper/knowledge-bases";
const DATA_MODELS_PATH = "/api/DataModels";
const REPORT_DEFINITIONS_TENANT_PATH = "/api/v1/report-definitions/tenant";
const EXTERNAL_WORKFLOWS_PATH = "/api/v1/external/workflows";
const INTENT_DEFINITIONS_PATH = "/api/intent-definitions";

const UNIVERSAL_AUTH_MISSING_MSG =
  "Universal CoPilot API key not configured in System Settings → Defaults.";

function resolveChatbotBaseUrl(): string {
  const envKey = "REACT_APP_API_URL_chatbot";
  const baseUrl = (process.env as Record<string, string | undefined>)[envKey]?.trim();
  if (!baseUrl) {
    throw new Error(`Missing environment variable: ${envKey}. Add it to your .env file.`);
  }
  return baseUrl;
}

function resolveExternalWorkflowsBaseUrl(): string {
  const envKey = "REACT_APP_API_URL_external_workflows";
  const baseUrl = (process.env as Record<string, string | undefined>)[envKey]?.trim();
  if (!baseUrl) {
    throw new Error(`Missing environment variable: ${envKey}. Add it to your .env file.`);
  }
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
}

function isNilGuid(id: string | undefined): boolean {
  if (!id) return true;
  return id.trim().toLowerCase() === NIL_GUID.toLowerCase();
}

function isUniversalKey(row: CopilotKeyDto): boolean {
  return row.keyType?.trim().toLowerCase() === "universal";
}

function normalizeCopilotKeys(res: unknown): CopilotKeyDto[] {
  if (Array.isArray(res)) return res as CopilotKeyDto[];
  if (res && typeof res === "object") {
    const obj = res as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as CopilotKeyDto[];
  }
  return [];
}

function pickUniversalCopilotKey(keys: CopilotKeyDto[]): CopilotKeyDto | undefined {
  const activeUniversal = keys.filter(
    (row) => isUniversalKey(row) && row.isActive !== false
  );
  if (activeUniversal.length === 0) return undefined;

  const withNilGuid = activeUniversal.find((row) => isNilGuid(row.copilotId));
  return withNilGuid ?? activeUniversal[0];
}

let universalAuthHeadersPromise: Promise<Record<string, string>> | null = null;

async function fetchUniversalCopilotAuthHeaders(): Promise<Record<string, string>> {
  const keys = normalizeCopilotKeys(await tenantDefaultsService.getCopilotKeys());
  const universal = pickUniversalCopilotKey(keys);
  const apiKey = universal?.key?.trim();
  const apiSecret = universal?.secret?.trim();
  if (!apiKey || !apiSecret) {
    throw new Error(UNIVERSAL_AUTH_MISSING_MSG);
  }
  return {
    "X-Api-Key": apiKey,
    "X-Api-Secret": apiSecret,
  };
}

/** Cached Universal key/secret headers for external workflow calls. */
function resolveUniversalCopilotAuthHeaders(): Promise<Record<string, string>> {
  if (!universalAuthHeadersPromise) {
    universalAuthHeadersPromise = fetchUniversalCopilotAuthHeaders().catch((err) => {
      universalAuthHeadersPromise = null;
      throw err;
    });
  }
  return universalAuthHeadersPromise;
}

async function externalWorkflowsGet<T>(url: string, params: Record<string, unknown> = {}): Promise<T> {
  const headers = await resolveUniversalCopilotAuthHeaders();
  const response = await apiClient.get(url, {
    params,
    baseURL: resolveExternalWorkflowsBaseUrl(),
    headers,
  });
  return response.data;
}

async function chatbotGet<T>(url: string, params: Record<string, unknown> = {}): Promise<T> {
  const response = await apiClient.get(url, {
    params,
    baseURL: resolveChatbotBaseUrl(),
  });
  return response.data;
}

async function chatbotPost<T>(url: string, data: unknown): Promise<T> {
  const response = await apiClient.post(url, data, {
    baseURL: resolveChatbotBaseUrl(),
  });
  return response.data;
}

async function chatbotPut<T>(url: string, data: unknown): Promise<T> {
  const response = await apiClient.put(url, data, {
    baseURL: resolveChatbotBaseUrl(),
  });
  return response.data;
}

async function chatbotDelete<T>(url: string): Promise<T> {
  const response = await apiClient.delete(url, {
    baseURL: resolveChatbotBaseUrl(),
  });
  return response.data;
}

export interface KnowledgeBaseItem {
  id: string;
  name: string;
  documents: number;
  scope: string;
}

export interface OrchestrationItem {
  id: string;
  name: string;
  description: string;
  trigger: string;
  code?: string;
  defaultChannel?: string;
  activationStatus?: string;
  status?: string;
}

export interface WorkflowStartContractField {
  id: string;
  key: string;
  label: string;
  dataType: string;
  required: boolean;
  defaultValue?: string;
  placeholder?: string;
  helpText?: string;
  options: string[];
}

export interface WorkflowStartContract {
  workflowId: string;
  name: string;
  description: string;
  status: string;
  revisionVersion?: number;
  inputNodeId?: string;
  inputNodeLabel?: string;
  triggerType?: string;
  channel: string;
  start: {
    kind?: string;
    inputMode?: string;
    fields: WorkflowStartContractField[];
    samplePayloadJson?: string | null;
  };
  runHints?: {
    payloadKeys?: string[];
    submitPath?: string;
    notes?: string;
  };
}

export interface DataModelItem {
  id: string;
  name: string;
  entities: string[];
  source: string;
}

export interface ReportCatalogItem {
  id: string;
  name: string;
  description: string;
}

interface KnowledgeBaseApiRecord {
  id?: string;
  Id?: string;
  title?: string;
  Title?: string;
  description?: string;
  Description?: string;
  tags?: string;
  Tags?: string;
  documentCount?: number;
  DocumentCount?: number;
}

interface DataModelApiRecord {
  id?: string;
  uuid?: string;
  modelName?: string;
  description?: string;
  type?: string;
  status?: string;
  tags?: string[];
}

interface ExternalWorkflowApiRecord {
  id?: string;
  name?: string;
  description?: string;
  status?: string;
  code?: string;
  activationStatus?: string;
  defaultChannel?: string | null;
  channels?: Record<string, number | null>;
}

interface WorkflowStartContractFieldApiRecord {
  id?: string;
  key?: string;
  label?: string;
  dataType?: string;
  required?: boolean;
  defaultValue?: string;
  placeholder?: string;
  helpText?: string;
  options?: unknown[];
}

interface WorkflowStartContractApiRecord {
  workflowId?: string;
  name?: string;
  description?: string;
  status?: string;
  revisionVersion?: number;
  inputNodeId?: string;
  inputNodeLabel?: string;
  triggerType?: string;
  start?: {
    kind?: string;
    inputMode?: string;
    fields?: WorkflowStartContractFieldApiRecord[];
    samplePayloadJson?: string | null;
  };
  runHints?: {
    payloadKeys?: string[];
    submitPath?: string;
    notes?: string;
  };
}

interface ReportDefinitionApiRecord {
  id?: string;
  Id?: string;
  _id?: string;
  report_id?: string;
  reportid?: string;
  name?: string;
  Name?: string;
  report_name?: string;
  ReportName?: string;
  description?: string;
  Description?: string;
  collectionname?: string;
  collectionName?: string;
}

function pickString(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
}

function coerceArray<T>(data: unknown, keys: string[]): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    for (const key of keys) {
      const value = record[key];
      if (Array.isArray(value)) return value as T[];
    }
  }
  return [];
}

function mapKnowledgeBase(raw: KnowledgeBaseApiRecord): KnowledgeBaseItem {
  const description = raw.description ?? raw.Description ?? "";
  const tags = raw.tags ?? raw.Tags ?? "";
  return {
    id: String(raw.id ?? raw.Id ?? ""),
    name: String(raw.title ?? raw.Title ?? ""),
    documents: Number(raw.documentCount ?? raw.DocumentCount ?? 0),
    scope: description || tags || "Knowledge base",
  };
}

function mapDataModel(raw: DataModelApiRecord): DataModelItem {
  const id = String(raw.id ?? raw.uuid ?? "");
  const name = String(raw.modelName ?? id);
  return {
    id,
    name,
    entities: Array.isArray(raw.tags) ? raw.tags.map(String).filter(Boolean) : [],
    source: raw.description?.trim() || raw.type?.trim() || "Data Model",
  };
}

function mapOrchestration(raw: ExternalWorkflowApiRecord): OrchestrationItem {
  const name = String(raw.name ?? "").trim();
  const description = String(raw.description ?? "").trim();
  const code = String(raw.code ?? "").trim();
  const defaultChannel = String(raw.defaultChannel ?? "").trim() || "sandbox";
  return {
    id: String(raw.id ?? "").trim(),
    name,
    description,
    trigger: code || name,
    ...(code ? { code } : {}),
    defaultChannel,
    ...(raw.activationStatus ? { activationStatus: String(raw.activationStatus) } : {}),
    ...(raw.status ? { status: String(raw.status) } : {}),
  };
}

function mapStartContractField(raw: WorkflowStartContractFieldApiRecord): WorkflowStartContractField {
  const options = Array.isArray(raw.options)
    ? raw.options.map((option) => String(option).trim()).filter(Boolean)
    : [];
  return {
    id: String(raw.id ?? "").trim(),
    key: String(raw.key ?? "").trim(),
    label: String(raw.label ?? raw.key ?? "").trim(),
    dataType: String(raw.dataType ?? "text").trim() || "text",
    required: Boolean(raw.required),
    defaultValue: raw.defaultValue != null ? String(raw.defaultValue) : undefined,
    placeholder: raw.placeholder != null ? String(raw.placeholder) : undefined,
    helpText: raw.helpText != null ? String(raw.helpText) : undefined,
    options,
  };
}

function mapStartContract(raw: WorkflowStartContractApiRecord, channel: string): WorkflowStartContract {
  const start = raw.start;
  const rawFields = start?.fields;
  const fields = Array.isArray(rawFields)
    ? rawFields.map(mapStartContractField).filter((field) => field.key || field.id)
    : [];
  return {
    workflowId: String(raw.workflowId ?? "").trim(),
    name: String(raw.name ?? "").trim(),
    description: String(raw.description ?? "").trim(),
    status: String(raw.status ?? "").trim(),
    revisionVersion: raw.revisionVersion,
    inputNodeId: raw.inputNodeId,
    inputNodeLabel: raw.inputNodeLabel,
    triggerType: raw.triggerType,
    channel,
    start: {
      kind: start?.kind,
      inputMode: start?.inputMode,
      fields,
      samplePayloadJson: start?.samplePayloadJson ?? null,
    },
    runHints: raw.runHints,
  };
}

function mapReportDefinition(raw: ReportDefinitionApiRecord): ReportCatalogItem {
  const id = pickString(
    raw as Record<string, unknown>,
    "id",
    "Id",
    "_id",
    "report_id",
    "reportid"
  );
  const name = pickString(
    raw as Record<string, unknown>,
    "name",
    "Name",
    "report_name",
    "ReportName"
  );
  const description =
    pickString(raw as Record<string, unknown>, "description", "Description") ||
    pickString(raw as Record<string, unknown>, "collectionname", "collectionName");
  return { id, name, description };
}

function coerceIntentList(data: unknown): IntentDefinitionListItem[] {
  if (Array.isArray(data)) return data.map(mapIntentListItem);
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    for (const key of ["intents", "data", "items", "results", "intentDefinitions", "intent_definitions", "intent_definations"]) {
      const value = record[key];
      if (Array.isArray(value)) return value.map(mapIntentListItem);
    }
  }
  return [];
}

function mapIntentListItem(raw: unknown): IntentDefinitionListItem {
  const record = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    _id: pickString(record, "_id", "id", "Id"),
    tenant_id: pickString(record, "tenant_id", "tenantId", "TenantId") || undefined,
    chatbot_id: pickString(record, "chatbot_id", "chatbotId", "ChatbotId"),
    name: pickString(record, "name", "Name"),
    code: pickString(record, "code", "Code"),
    status: pickString(record, "status", "Status"),
    confidence_threshold: Number(record.confidence_threshold ?? record.confidenceThreshold ?? 0),
    updated_at: pickString(record, "updated_at", "updatedAt", "UpdatedAt") || undefined,
  };
}

function unwrapIntentRecord(data: unknown): IntentDefinitionRecord {
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    const nested =
      record.data ??
      record.Data ??
      record.intent ??
      record.intentDefinition ??
      record.intent_definition;
    if (nested && typeof nested === "object") {
      return nested as IntentDefinitionRecord;
    }
    if (record._id != null || record.id != null) {
      return data as IntentDefinitionRecord;
    }
  }
  throw new Error("Invalid intent definition response");
}

function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  const map = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    if (!map.has(item.id)) {
      map.set(item.id, item);
    }
  }
  return Array.from(map.values());
}

export const intentDefinitionService = {
  async getKnowledgeBases(): Promise<KnowledgeBaseItem[]> {
    const data = await apiService.get(KNOWLEDGE_BASES_PATH, {}, "backend");
    const success = data?.success ?? data?.Success;
    const list = data?.data ?? data?.Data;

    if (success === false) {
      const msg = data?.message ?? data?.Message ?? "Failed to load knowledge bases";
      throw new Error(typeof msg === "string" ? msg : "Failed to load knowledge bases");
    }

    if (!Array.isArray(list)) return [];
    return list.map((item: KnowledgeBaseApiRecord) => mapKnowledgeBase(item));
  },

  async getOrchestrations(): Promise<OrchestrationItem[]> {
    return this.listExternalWorkflows();
  },

  async listExternalWorkflows(): Promise<OrchestrationItem[]> {
    const data = await externalWorkflowsGet<unknown>(EXTERNAL_WORKFLOWS_PATH);
    const list = coerceArray<ExternalWorkflowApiRecord>(data, ["items", "data", "workflows", "results"]);
    return list.map(mapOrchestration).filter((item) => item.id);
  },

  async getWorkflowStartContract(
    workflowId: string,
    channel = "sandbox"
  ): Promise<WorkflowStartContract> {
    const id = workflowId.trim();
    if (!id) throw new Error("Workflow id is required.");
    const resolvedChannel = channel.trim() || "sandbox";
    const data = await externalWorkflowsGet<WorkflowStartContractApiRecord>(
      `${EXTERNAL_WORKFLOWS_PATH}/${encodeURIComponent(id)}/start-contract`,
      { channel: resolvedChannel }
    );
    const contract = mapStartContract(data ?? {}, resolvedChannel);
    return {
      ...contract,
      workflowId: contract.workflowId || id,
    };
  },

  async getDataModels(): Promise<DataModelItem[]> {
    const data = await apiService.get(DATA_MODELS_PATH, {}, "backend");
    const list = coerceArray<DataModelApiRecord>(data, ["dataModels", "DataModels", "data"]);
    return list
      .filter((item) => !item.status || item.status.toLowerCase() === "active")
      .map(mapDataModel)
      .filter((item) => item.id);
  },

  async getReportDefinitions(): Promise<ReportCatalogItem[]> {
    const data = await apiService.get(REPORT_DEFINITIONS_TENANT_PATH, {}, "analytics_hub");
    const success = data?.success ?? data?.Success;
    const list = data?.data ?? data?.Data;

    if (success === false) {
      const msg = data?.message ?? data?.Message ?? "Failed to load report definitions";
      throw new Error(typeof msg === "string" ? msg : "Failed to load report definitions");
    }

    if (!Array.isArray(list)) return [];
    return list.map((item: ReportDefinitionApiRecord) => mapReportDefinition(item)).filter((item) => item.id);
  },

  async listIntentDefinitions(chatbotId: string): Promise<IntentDefinitionListItem[]> {
    const data = await chatbotGet<unknown>(INTENT_DEFINITIONS_PATH, { chatbot_id: chatbotId });
    return coerceIntentList(data).filter((item) => item._id);
  },

  async getIntentDefinitionById(intentId: string): Promise<IntentDefinitionRecord> {
    const data = await chatbotGet<unknown>(
      `${INTENT_DEFINITIONS_PATH}/${encodeURIComponent(intentId)}`
    );
    return unwrapIntentRecord(data);
  },

  async loadIntentForEdit(intentId: string): Promise<ParsedIntentLoadResult> {
    const record = await this.getIntentDefinitionById(intentId);
    return parseIntentDefinitionResponse(record);
  },

  mergeCatalogById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
    return mergeById(existing, incoming);
  },

  validateIntentForm(snapshot: IntentFormSnapshot): string | null {
    return validateIntentForm(snapshot);
  },

  buildPayload(snapshot: IntentFormSnapshot): IntentDefinitionPayload {
    return buildIntentDefinitionPayload(snapshot);
  },

  parseRecord(record: IntentDefinitionRecord) {
    return parseIntentDefinitionRecord(record);
  },

  async upsertIntentDefinition(snapshot: IntentFormSnapshot): Promise<IntentDefinitionRecord> {
    const validationError = validateIntentForm(snapshot);
    if (validationError) {
      throw new Error(validationError);
    }

    const intentId = snapshot.intentId?.trim();
    const payload = buildIntentDefinitionPayload(snapshot);

    // Always log the exact outgoing body so Request vs Response can be compared in DevTools.
    // eslint-disable-next-line no-console
    console.info("[intent-save] OUTGOING PUT/POST body:\n", JSON.stringify(payload, null, 2));

    const data = intentId
      ? await chatbotPut<unknown>(
          `${INTENT_DEFINITIONS_PATH}/${encodeURIComponent(intentId)}`,
          payload
        )
      : await chatbotPost<unknown>(INTENT_DEFINITIONS_PATH, payload);

    return unwrapIntentRecord(data);
  },

  async deleteIntentDefinition(intentId: string): Promise<void> {
    const id = intentId?.trim();
    if (!id) {
      throw new Error("Intent id is required.");
    }
    await chatbotDelete<unknown>(`${INTENT_DEFINITIONS_PATH}/${encodeURIComponent(id)}`);
  },
};

export default intentDefinitionService;
